<script setup lang="ts">
import type { WorkersApiLegacy } from '@rialtic/types'
import { useBrowserLocation } from '@vueuse/core'
import type { InfluencingLineDetails, RawInsights } from '../types'
import InsightDetails from './InsightDetails.vue'

const props = defineProps<{
  insights: RawInsights
  isConsoleUser?: boolean
  expand?: boolean
}>()

const recommendedInsight = computed(() =>
  props.insights?.find((insight) => insight.is_recommendation),
)

const location = useBrowserLocation()

const isConsoleUrl = computed(() =>
  /:3000|console/g.test(location.value.host || ''),
)

const items = computed(() =>
  props.insights.map((insight, index) => ({
    value: insight.raw_insight_id,
    insight,
    index,
  })),
)
const openItems = computed(() =>
  props.expand ? items.value.map(({ value }) => value) : [],
)

const getInfluencingLines = (
  historyInfo: WorkersApiLegacy.HistoryInfo[] | undefined,
): InfluencingLineDetails[] => {
  const influencingLines: InfluencingLineDetails[] = []

  if (!historyInfo || !historyInfo.length) return influencingLines

  for (const info of historyInfo) {
    if (info?.lines?.length) {
      for (const line of info.lines) {
        if (!Object.hasOwn(line, 'exit'))
          influencingLines.push({
            claimId: info.claimId,
            sequence: line.seq,
          })
      }
    }
  }

  const uniqueInfluencingLines = Array.from(
    new Map(
      influencingLines.map((line) => [
        `${line.claimId}-${line.sequence}`,
        line,
      ]),
    ).values(),
  )

  return uniqueInfluencingLines
}
</script>

<template>
  <section v-if="insights" class="flex flex-col gap-2">
    <UAccordion
      v-if="isConsoleUser"
      :default-value="openItems"
      :items="items"
      :ui="{
        item: 'overflow-hidden rounded-lg border border-neutral-100 mb-2 last:border-b',
      }"
      type="multiple"
    >
      <template #default="{ item: { insight, index } }">
        <div class="flex w-full">
          <div class="mr-4 text-neutral-600">{{ index + 1 }}</div>
          <div class="flex flex-col">
            <p class="text-left text-neutral-900">
              {{
                insight.insight.insight.paapi_policy_name ||
                insight.insight.insight.policy_name
              }}
            </p>
            <div class="mt-2 flex gap-2">
              <UBadge
                v-if="insight.excluded_by && insight.excluded_by.length"
                class="bg-warning-100 text-warning-800 capitalize"
                label="excluded"
                size="sm"
                variant="soft"
              />
              <UBadge
                v-if="insight.is_recommendation"
                color="primary"
                label="Selected insight"
                size="sm"
                variant="solid"
              />
              <UBadge
                v-if="insight.insight.insight_mode"
                class="capitalize"
                :class="{
                  'bg-primary-75 text-primary-900':
                    insight.insight.insight_mode === 'Observation',
                }"
                :color="
                  insight.insight.insight_mode === 'Active'
                    ? 'primary'
                    : 'neutral'
                "
                :label="insight.insight.insight_mode"
                :variant="
                  insight.insight.insight_mode === 'Active' ? 'solid' : 'soft'
                "
                size="sm"
              />
            </div>
          </div>
        </div>
      </template>
      <template #content="{ item: { insight } }">
        <div class="p-4">
          <InsightDetails
            :description="insight.insight.insight.description"
            :influencing-lines="
              getInfluencingLines(insight.insight.insight.historyInfo)
            "
            :policy-id="insight.insight.policy_id"
          />

          <AuthPermissionsGuard
            :permissions="[
              PERMISSIONS.writeTeamContent,
              PERMISSIONS.readSystem,
            ]"
          >
            <section
              class="border-primary mt-4 border-l-2 py-1"
              data-testid="StatusInsights__insightView"
            >
              <h3
                class="text-primary text-md mb-2 flex items-center gap-1 pl-2"
              >
                <RialticLogo class="inline-block w-5" icon />
                Viewable by Rialtic only
              </h3>
              <div class="pl-2">
                <div class="mb-4 w-full rounded-lg bg-neutral-50 px-2 py-5">
                  <dl
                    class="grid grid-cols-[repeat(auto-fill,minmax(10rem,1fr))] gap-4 text-sm text-neutral-900"
                  >
                    <template
                      v-for="[title, value] in [
                        ['Engine', insight.insight.engine_id],
                        ['Insight mode', insight.insight.insight_mode],
                        /* Wrap in `` to show undefined & null */
                        ['Insight score', `${insight.insight_score}`],
                        ['Insight type', insight.insight.insight.type],
                        ['Policy name', insight.insight.paapi_policy_name],
                        ['Policy ID', insight.insight.policy_id],
                      ]"
                      :key="title"
                    >
                      <div v-if="value" class="min-w-0">
                        <dt class="font-medium">{{ title }}</dt>
                        <dd class="break-words font-normal">{{ value }}</dd>
                      </div>
                    </template>
                  </dl>
                </div>

                <h4 class="mb-2 text-neutral-800">Traces</h4>

                <div class="mb-4">
                  <div
                    v-for="(trace, i) in insight.insight.insight.trace"
                    :key="i"
                  >
                    <div class="mr-2 inline-block">
                      <div
                        class="flex h-full items-center justify-start space-x-1 rounded-full border border-neutral-200 bg-neutral-100 px-3 py-1"
                      >
                        <p
                          class="whitespace-nowrap text-center text-xs leading-none text-neutral-500"
                        >
                          Tree name
                        </p>
                        <p
                          class="text-center text-xs font-medium leading-none text-neutral-900"
                        >
                          {{ trace.tree_name || '-' }}
                        </p>
                      </div>
                    </div>
                    <div class="mb-4 inline-block">
                      <div
                        class="flex h-full items-center justify-start space-x-1 rounded-full border border-neutral-200 bg-neutral-100 px-3 py-1"
                      >
                        <p
                          class="text-center text-xs leading-none text-neutral-500"
                        >
                          End Label
                        </p>
                        <p
                          class="text-center text-xs font-medium leading-none text-neutral-900"
                        >
                          {{ trace.end_label }}
                        </p>
                      </div>
                    </div>

                    <ul class="flex w-full flex-col space-y-2 pl-5">
                      <li
                        v-for="(
                          [note, pass, code, filteredNote], j
                        ) in trace.traversal"
                        :key="j"
                        class="flex items-start"
                      >
                        <div class="w-6 pt-1">
                          <UIcon
                            v-if="pass === 'YES'"
                            name="i-ph-check-circle-bold"
                            class="text-secondary size-6"
                          />
                          <UIcon
                            v-else
                            name="i-ph-x-circle-bold"
                            class="text-error size-6"
                          />
                        </div>

                        <p class="flex-1 px-2 py-1 text-sm">
                          {{ note }}
                          <br />
                          <span class="text-xs text-neutral-700">
                            {{ filteredNote }}
                          </span>
                        </p>
                        <p class="py-1 pl-2 text-xs text-neutral-700">
                          {{ code }}
                        </p>
                      </li>
                    </ul>
                  </div>
                </div>

                <template v-if="isConsoleUrl">
                  <h4 class="mb-2 text-neutral-800">History Info</h4>

                  <!-- Component exists only in console, won't be displayed in provider inquiries -->
                  <LazyInsightHistoryInfo
                    :history-info="insight.insight.insight.historyInfo || []"
                    data-testid="StatusInsights__LazyInsightHistoryInfo"
                  />
                </template>
              </div>
            </section>
          </AuthPermissionsGuard>
        </div>
      </template>
    </UAccordion>
    <InsightDetails
      v-else-if="recommendedInsight"
      :description="recommendedInsight.insight.insight.description"
      :influencing-lines="
        getInfluencingLines(recommendedInsight.insight.insight.historyInfo)
      "
    />
  </section>
</template>
